import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';

import InlineEditingWrapper from 'components/inline-editing-wrapper/InlineEditingWrapper';
import Input from 'components/inputs/input/Input';
import MoreButton from 'components/buttons/more-button/MoreButton';
import RichTextEditor from 'components/inputs/rich-text-editor/RichTextEditor';
import { withContextMenu } from 'components/context-menu/ContextMenuHOC';

import { hideConfirmModal, showConfirmModal } from 'redux-store/reducers/modal-reducer';

import { getDisplayDateAndTime } from 'utils/date';
import { getTruncatedName } from 'utils/view';
import { isRequired } from 'utils/validators';
import { __t, applyTranslations } from 'utils/language';

import type { ContextMenuItem, Note as NoteAlias } from 'types';

import './note.scss';

applyTranslations(require('./notes-note.i18n.json'));

type OwnProps = {
  note: NoteAlias,
  editorClassName?: string,
  hideOrganizationName?: boolean,
  isEditModeAllowed?: boolean,
  contextMenuItems?: Array<ContextMenuItem>,
  onCancel: (a: void) => void,
  onDelete: (a: NoteAlias) => void,
  onEdit: (b: NoteAlias, a: string) => void,
  onModified: (a: number) => void
};

type DispatchProps = {
  dispatchShowConfirmationModal: (
    params: {
      message: string,
      title: string,
      confirmButtonLabel: string,
      onCancel: (a: void) => void,
      onConfirm: (a: void) => void
    }
  ) => void,
  dispatchHideConfirmationModal: () => void
};

type Props = OwnProps & DispatchProps;

type State = {
  isEditMode: boolean
};

const MoreButtonWithMenu = withContextMenu(MoreButton, {
  position: 'bottom',
  forceEventPropagation: true
});

class Note extends React.PureComponent<Props, State> {
  static defaultProps = {
    editorClassName: '',
    contextMenuItems: []
  };

  state: State = {
    isEditMode: false
  };

  onEditNote = (value: any): void => {
    const { note, onEdit } = this.props;

    this.toggleEditMode();
    onEdit(note, String(value));
  };

  toggleEditMode = (): void => {
    const { note, onModified } = this.props;

    this.setState(state => ({
      isEditMode: !state.isEditMode
    }));

    onModified(note.id);
  };

  handleCancel = (): void => {
    const { onCancel } = this.props;
    this.toggleEditMode();

    if (onCancel) {
      onCancel();
    }
  };

  handleDelete = (): void => {
    const { note, dispatchShowConfirmationModal, dispatchHideConfirmationModal, onDelete } = this.props;

    dispatchShowConfirmationModal({
      title: __t('notes_note.modal_delete_title'),
      message: __t('notes_note.modal_delete_message'),
      confirmButtonLabel: __t('notes_note.modal_delete_action'),
      onCancel: () => {
        dispatchHideConfirmationModal();
      },
      onConfirm: () => {
        onDelete(note);
        dispatchHideConfirmationModal();
      }
    });
  };

  renderContextMenu() {
    const { note, contextMenuItems } = this.props;

    const editItem: ContextMenuItem = {
      name: 'edit',
      text: __t('notes_note.context_menu_note_edit'),
      meta: {
        noteId: note.id
      },
      onClick: this.toggleEditMode
    };

    const deleteItem: ContextMenuItem = {
      name: 'delete',
      text: __t('notes_note.context_menu_note_delete'),
      meta: {
        noteId: note.id
      },
      onClick: this.handleDelete
    };

    const separator: ContextMenuItem = {
      type: 'separator'
    };

    const items: Array<ContextMenuItem> = [
      editItem,
      separator,
      deleteItem
    ].concat(contextMenuItems ?? []);

    return <MoreButtonWithMenu width={24} contextMenuProps={{ items, zIndex: 100 }} />;
  }

  renderNoteView = () => (
    <div className="note__message">
      <RichTextEditor
        key={this.props.note.text}
        canEdit={false}
        value={this.props.note.text}
      />
    </div>
  );

  renderNoteEdit = () => (
    <Input
      width="responsive"
      autoFocus
      multiline
      value="" // will be overwritten in InlineEditingWrapper
    />
  );

  render(): React.ReactNode {
    const { editorClassName, hideOrganizationName, note, isEditModeAllowed } = this.props;
    const { isEditMode } = this.state;
    const updatedAt = note.updatedAt || note.createdAt;
    const updatedBy = note.updatedBy || note.createdBy;
    const inEditMode = isEditModeAllowed && isEditMode;
    const canEnterEditMode = isEditModeAllowed && !isEditMode;

    const noteClasses = classnames({
      'note': true,
      'note--editable': canEnterEditMode
    });

    const noteHeaderClasses = classnames({
      'note__header': true,
      'note__header--edited': note.isEdited
    });

    return (
      <div className={noteClasses}>
        <div className="note__container">
          <div className={noteHeaderClasses}>
            <span className="note__meta">{getDisplayDateAndTime(updatedAt)}</span>
            {
              !hideOrganizationName && (
                <span className="note__meta">
                  {note.organizationName}
                </span>
              )
            }
            <span className="note__meta">{getTruncatedName(updatedBy)}</span>
          </div>
          {
            inEditMode ? (
              <div className={editorClassName}>
                <InlineEditingWrapper
                  inGlobalEditMode={false}
                  onCancel={this.handleCancel}
                  onSubmit={this.onEditNote}
                  renderViewMode={this.renderNoteView}
                  renderEditMode={this.renderNoteEdit}
                  initialValue={note.text}
                  inForceEditMode
                  validatorResolvers={[
                    (value) => isRequired(value)
                  ]}
                />
              </div>
            ) : (
              this.renderNoteView()
            )
          }
        </div>
        {
          canEnterEditMode && (
            <div className="note__control">
              {this.renderContextMenu()}
            </div>
          )
        }
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchShowConfirmationModal: (params) => dispatch(showConfirmModal(params)),
  dispatchHideConfirmationModal: () => dispatch(hideConfirmModal())
});

export default connect(null, mapDispatchToProps)(Note);
